import React, { useRef, useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { CheckCircle2, Info, Bell } from "lucide-react";

import PageTitle from "components/generic/PageTitle";
import QuickActionCard from "components/generic/QuickActions";
import Breadcrumbs from "components/generic/Breadcrumbs";
import SuccessfulSubmission from "components/generic/SuccessfulSubmission";

import TaskForm from "components/crewManagement/TaskForm";
import InfoCard from "components/crewManagement/InfoCard";

import { postAssignTask } from "lib/api/crewApi";
import { cleanBio } from "lib/utils";

const content = () => {
  return (
    <ul className="list-disc pl-6">
      <li className="text-grey-primary text-xs">Be specific and clear in instructions</li>
      <li className="text-grey-primary text-xs">Set realistic deadlines.</li>
      <li className="text-grey-primary text-xs">Include all necessary resources.</li>
      <li className="text-grey-primary text-xs">Communicate priority clearly.</li>
    </ul>
  )
}

const AddTaskPage = () => {
  const location = useLocation();
  const { crewMember } = location.state || {}
  const navigate = useNavigate();

  const formRef = useRef(null);

  const handleSubmit = async (formData) => {
  // 1️⃣ Get user from localStorage
  const userStr = localStorage.getItem("user");
  const user = userStr ? JSON.parse(userStr) : null;

  // 2️⃣ Base params
  const params = {
    ...formData,
    assigned_to: crewMember.crew_member_id,
    status: "assigned",
    send_email: 1,
  };

  // 3️⃣ Conditionally add organization_type
  if (user?.organizationType === 2) {
    params.organization_type = 2;
  }

  try {
    const response = await postAssignTask(params);
    console.log("Task created:", response);

    const payload = {
      task: response.data,
      crewMember,
    };

    setIsComplete(true);
    setSuccessPayload(payload);

  } catch (err) {
    console.error("Task create error", err);
  }
};


  const handleExternalSubmit = () => {
    if (formRef.current) {
      formRef.current.requestSubmit();
    }
  };

  const [isComplete, setIsComplete] = useState(false)
  const [profileImage, setProfileImage] = useState("/images/misc/profile.png");
  const [successPayload, setSuccessPayload] = useState(null);

  useEffect(() => {
    if (crewMember?.crew_member_files?.length > 0) {
    const image = crewMember.crew_member_files.find(file => file.file_type === "profile_photo");

    if (image) {
      if (image.file_path.includes("http")) {
        setProfileImage(image.file_path);
      }
      else {
        const s3FullUrl = `${process.env.REACT_APP_S3_BASE_URL}/${image.file_path}`;
        setProfileImage(s3FullUrl);
      }
    }
  }
  }, [crewMember])
  return (
    <div className="add-task-content p-6">
      {isComplete ?
        (
          <SuccessfulSubmission
            title={"Task Assigned Successfully"}
            description={"You have created and assigned a new task to Sarah Johnson."}
            buttonText={"View Task"}
            onConfirm={() =>
              navigate("/crew-management/view-task", {
                state: successPayload
              })
            }
            onClose={() => navigate(-1)}
          />
        ) :
        (<>
          <Breadcrumbs />
          <PageTitle title={"Assign New Task"} description={`Create and assign a task to ${crewMember.first_name + " " + crewMember.last_name}`} />
          <div className="flex gap-6 self-stretch ">
            <div className="w-4/6">
              <TaskForm passRef={formRef} handleSubmit={handleSubmit} />
            </div>
            <div className="w-2/6 flex flex-col justify-start items-start gap-6">
              <div className="flex flex-col justify-start items-start gap-4 bg-card rounded-2xl p-6 w-full">
                <div className=" text-2xl leading-normal font-semibold">Assigned To</div>
                <div
                  className="w-full bg-button-generic rounded-xl border border-button-generic drop-shadow-sm p-3 flex items-center gap-3 mb-3 last:mb-0"
                >
                  <img
                    src={profileImage}
                    alt={crewMember.first_name + " " + crewMember.last_name}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-base font-semibold">
                      {crewMember.first_name + " " + crewMember.last_name}
                    </p>
                    <p
                      className="text-sm text-grey-primary overflow-hidden text-ellipsis whitespace-normal break-words"
                    >
                      {cleanBio(crewMember.bio)}
                    </p>
                  </div>
                </div>
              </div>
              <QuickActionCard
                handleClick={handleExternalSubmit}
                buttonText={"Assign Task"}
                icon={<CheckCircle2 className="w-4 h-4 " />}
              />
              <InfoCard
                icon={<Info className="w-4 h-4 text-card-foreground" />}
                title={"Task Guidelines"}
                content={content()}
                bgColor={"bg-card"}
                titleColor={"text-card-foreground"}
              />
              <InfoCard
                icon={<Bell className="w-4 h-4 text-yellow-600" />}
                title={"Notification"}
                content={`${crewMember.first_name} ${crewMember.last_name} will receive an email and in-app notification when you assign this task.`}
                bgColor={"bg-yellow-50"}
                titleColor={"text-yellow-600"}
              />
            </div>
          </div>
        </>
        )
      }
    </div>
  );
};

export default AddTaskPage;
